import type { ElementType, ReactNode } from 'react';
import { Box, Group, Paper, type PaperProps, Stack, Text, Title } from '@mantine/core';
import { sourceMonth } from '~lib';
import { ArrowMark } from './ArrowButton';
import { EntityLogo, type EntityLogoProps } from './EntityLogo';

/**
 * The directory card and row (decision D-3; gap register O11, O13, R6, R7; frames `301:58186`,
 * `359:18439`): the shape every listing and carousel entry takes. The card is drawn at ≈354×224
 * with a 16px radius, the row at ≈80px with no header; both end in the arrow mark.
 *
 * The whole card (or row) is one link, so the hover wash is the link's own hover (`[data-ui-
 * directory-card]` in `primitive-styles.tsx`) — not a card hovering around a link. A control that
 * must sit on the card (the follow bookmark) is a sibling laid over the corner, never a child of
 * the anchor: nested interactive content is invalid HTML and a double tab stop.
 */

/**
 * @desc How an entry links. Plain markup (a fragment) passes `linkProps={{ href, 'data-spa-link':
 * true }}` and keeps the default `<a>`; the portal passes its router `Link` as `linkComponent`
 * with `{ to, params, search }`, so the typed destination is checked in the portal's wrapper.
 */
/**
 * @desc What Mantine's polymorphic `component` is typed as here. The host is really any anchor
 * component (`a`, a router `Link`); typing it as `a` keeps Mantine's props anchor-shaped, and the
 * host's own props arrive untyped through `linkProps` — their checking is the caller's (the
 * portal's wrapper types `to`/`params`/`search` against its route tree).
 */
export type AnchorHost = 'a';

export type DirectoryLink = {
  /** @desc The anchor host. Defaults to `a`. */
  linkComponent?: ElementType;
  /** @desc The host's own props: `href` for an `<a>`, `to`/`params`/`search` for a router link. */
  linkProps: Record<string, unknown>;
};

export type DirectoryEntryProps = DirectoryLink & {
  logo: EntityLogoProps;
  /** @desc The line above the name: the parent group, or the continent. */
  eyebrow: ReactNode;
  name: string;
  /** @desc Clamped to two lines. */
  description?: ReactNode;
  lastUpdated?: Date;
  /** @desc Beside the name — a "Sold" badge, an Illustrative badge. */
  badge?: ReactNode;
  /** @desc A short fact at the foot (the ticker code); hidden below `sm` on the row. */
  meta?: ReactNode;
  /** @desc A control that sits on the entry but outside its link (the follow bookmark). */
  aside?: ReactNode;
};

export type DirectoryCardProps = DirectoryEntryProps & {
  /**
   * @desc The entry that is the current page (the brand carousel's own brand, C10): filled
   * Primary 20, no arrow and no link — `aria-current="page"` instead.
   */
  current?: boolean;
};

/** @desc "Last updated: September 2026" — the month, as the frames print it. */
export const lastUpdatedText = (at: Date) => `Last updated: ${sourceMonth(at)}`;

/** @desc The card is a column: head, description, and the foot pinned to the bottom. */
const CARD_LAYOUT = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--mantine-spacing-sm)',
} as const;

const CARD_STYLES: PaperProps['styles'] = { root: CARD_LAYOUT };

/**
 * @desc The current entry: the Primary 20 fill a tinted control uses (`--charlie-control-fill`,
 * navy-1 in light, dark-5 in the dark and blue schemes), border included.
 */
const CURRENT_STYLES: PaperProps['styles'] = {
  root: {
    ...CARD_LAYOUT,
    backgroundColor: 'var(--charlie-control-fill)',
    borderColor: 'var(--charlie-control-fill)',
  },
};

/**
 * @desc Figma's directory card (decision D-3, gaps O11, R6; `301:58186`): a 16px-radius card with
 * a 40px logo or flag, a 14px eyebrow, the name at the card title size (serif, D-1), a two-line
 * description and the arrow mark bottom-right; the whole card is one link. `aside` is laid over the
 * top-right corner as a sibling of the link, so a bookmark on a card is a separate tab stop and the
 * card's accessible name stays its own.
 */
export function DirectoryCard({
  linkComponent = 'a',
  linkProps,
  logo,
  eyebrow,
  name,
  description,
  lastUpdated,
  badge,
  meta,
  aside,
  current = false,
}: DirectoryCardProps) {
  const body = (
    <>
      <Group gap="sm" wrap="nowrap" align="center" pr={aside ? 40 : undefined}>
        <EntityLogo size={40} {...logo} />
        <Stack gap={0} miw={0}>
          <Text size="sm" c="dimmed" truncate component="span">
            {eyebrow}
          </Text>
          <Group gap="xs" wrap="nowrap" align="baseline">
            <Title order={3} lineClamp={1}>
              {name}
            </Title>
            {badge}
          </Group>
        </Stack>
      </Group>
      {description && (
        <Text size="sm" lineClamp={2} component="p" m={0}>
          {description}
        </Text>
      )}
      <Group justify="space-between" align="flex-end" gap="sm" wrap="nowrap" mt="auto">
        <Group gap="sm" wrap="wrap">
          {meta && (
            <Text size="xs" c="dimmed" component="span">
              {meta}
            </Text>
          )}
          {lastUpdated && (
            <Text size="xs" c="dimmed" component="span">
              {lastUpdatedText(lastUpdated)}
            </Text>
          )}
        </Group>
        {!current && <ArrowMark />}
      </Group>
    </>
  );

  return (
    <Box pos="relative" h="100%">
      {current ? (
        <Paper
          withBorder
          p="md"
          mih={224}
          h="100%"
          styles={CURRENT_STYLES}
          aria-current="page"
          data-current
        >
          {body}
        </Paper>
      ) : (
        <Paper
          component={linkComponent as AnchorHost}
          {...linkProps}
          withBorder
          p="md"
          mih={224}
          h="100%"
          c="inherit"
          td="none"
          styles={CARD_STYLES}
          aria-label={name}
          data-ui-directory-card
        >
          {body}
        </Paper>
      )}
      {aside && (
        <Box pos="absolute" top="var(--mantine-spacing-md)" right="var(--mantine-spacing-md)">
          {aside}
        </Box>
      )}
    </Box>
  );
}
